import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Shield, Zap, LogIn } from 'lucide-react';
import { Button } from '../ui';
import { NavbarLogo } from '../ui/Logo';

/**
 * @component Navbar
 * @description Barra de navegación principal optimizada para MVP comercial bundle-first
 * Especificaciones: Consistencia visual, responsive, Core Web Vitals optimizado
 */

interface NavbarProps {
  transparent?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ transparent = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  
  // Detectar scroll para cambiar el fondo
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Llamada inicial para establecer el estado correcto
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Cerrar el menú móvil cuando cambia la ruta
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);
  
  // Prevenir el desplazamiento cuando el menú está abierto
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);
  
  // Ajustar la clase de fondo según props y estado de scroll
  const navBgClass = transparent && !isScrolled
    ? 'bg-transparent'
    : 'bg-white shadow-sm';
  
  // Ajustar el color del texto según si es transparente o no
  const textColorClass = transparent && !isScrolled
    ? 'text-white'
    : 'text-gray-800';
  
  // Opciones de navegación para MVP comercial bundle-first
  const navItems = [
    { name: 'Inicio', path: '/' },
    { name: 'Emergencias', path: '/sos' },
    { name: 'Producto SOS', path: '/producto/sos' },
    { name: 'Packs', path: '/packs' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Soporte', path: '/soporte' },
  ];

  // CTAs principales para conversión
  const ctaItems = [
    { name: 'Activar', path: '/activar', variant: 'outline' as const },
    { name: 'Comprar', path: '/comprar', variant: 'default' as const },
  ];
  
  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${navBgClass} pt-safe-top`}
      aria-label="Navegación principal"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <NavbarLogo className={`transition-opacity duration-300 ${transparent && !isScrolled ? 'opacity-90' : 'opacity-100'}`} />
          </div>
          
          {/* Enlaces para pantallas medianas y grandes */}
          <div className="hidden md:flex md:items-center md:space-x-6 lg:space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`${textColorClass} hover:text-primary-600 px-3 py-2 text-sm font-medium`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* CTAs principales para conversión */}
            <div className="flex items-center space-x-2 lg:space-x-3">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/auth/login" className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  Iniciar sesión
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/tutorial-activacion" className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Activar
                </Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/comprar">
                  Comprar
                </Link>
              </Button>
            </div>
          </div>
          
          {/* Botón de menú móvil */}
          <div className="flex items-center md:hidden">
            <button
              type="button"
              className={`${textColorClass} inline-flex items-center justify-center p-2 rounded-md hover:bg-primary-100 hover:text-primary-600 active:bg-primary-200 focus:outline-none touch-manipulation mobile-touch-target`}
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Alternar menú"
            >
              {isMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Menú móvil */}
      <div
        className={`md:hidden fixed inset-0 z-40 pt-16 bg-white transform transition-transform ease-in-out duration-300 ${
          isMenuOpen ? 'translate-y-0' : '-translate-y-full'
        }`}
        aria-hidden={!isMenuOpen}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 bg-white h-full overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className="block px-3 py-4 rounded-md text-base font-medium text-gray-800 hover:bg-primary-50 active:bg-primary-100 touch-manipulation"
              onClick={() => setIsMenuOpen(false)}
            >
              {item.name}
            </Link>
          ))}
          <div className="pt-4 pb-3 border-t border-gray-200 space-y-3">
            <Button variant="ghost" className="w-full" asChild>
              <Link
                to="/auth/login"
                className="flex items-center justify-center gap-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <LogIn className="h-4 w-4" />
                Iniciar sesión
              </Link>
            </Button>
            <Button variant="outline" className="w-full" asChild>
              <Link
                to="/tutorial-activacion"
                className="flex items-center justify-center gap-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <Zap className="h-4 w-4" />
                Tutorial de activación
              </Link>
            </Button>
            <Button className="w-full" asChild>
              <Link
                to="/comprar"
                onClick={() => setIsMenuOpen(false)}
              >
                Comprar Ahora
              </Link>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Overlay para cerrar el menú al hacer clic fuera */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-30"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </nav>
  );
};

export default Navbar;
